import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { SHEET_BREAKPOINT_PX } from '../theme/tokens.js';

/**
 * The two media queries the primitives care about, and a subscription to them.
 *
 * The breakpoint one is load bearing: it is what makes a Dialog render as a
 * Sheet on a phone without a call site opting in. A prop would eventually be
 * forgotten on one screen, and that screen would be the one a scorekeeper
 * opens in a gym.
 */

/**
 * Below the breakpoint, not at it. 640px itself is a Dialog.
 *
 * Written as `max-width: 639.98px` rather than the exact `width < 640px` range
 * syntax because the range form is newer, and the failure mode of a query a
 * browser cannot parse is silent: it never matches, and every phone gets a
 * centered modal. The 0.02 covers fractional viewport widths on scaled
 * displays.
 */
export const SHEET_MEDIA_QUERY = `(max-width: ${String(SHEET_BREAKPOINT_PX - 0.02)}px)`;

export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function mediaQueryList(query: string): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(query);
}

/** Read a media query once. False where there is no DOM to ask. */
export function matchesMediaQuery(query: string): boolean {
  const list = mediaQueryList(query);
  return list !== null && list.matches;
}

function createSubscribe(query: string): (onChange: () => void) => () => void {
  return (onChange) => {
    const list = mediaQueryList(query);
    if (list === null) return () => undefined;
    list.addEventListener('change', onChange);
    return () => {
      list.removeEventListener('change', onChange);
    };
  };
}

/** Server snapshot: no DOM, so no match. A phone corrects itself on hydration. */
function getServerSnapshot(): boolean {
  return false;
}

export function useMediaQuery(query: string): boolean {
  const subscribe = useMemo(() => createSubscribe(query), [query]);
  const getSnapshot = useCallback(() => matchesMediaQuery(query), [query]);
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * True below 640px, which is the whole of rubric C10: Dialogs render as bottom
 * Sheets. Exported because the scorebook has its own layout decisions to make
 * against the same line.
 */
export function useIsCompactViewport(): boolean {
  return useMediaQuery(SHEET_MEDIA_QUERY);
}

export function useReducedMotion(): boolean {
  return useMediaQuery(REDUCED_MOTION_QUERY);
}
